
import React from 'react';

const baseFeatures = [
  {
    title: 'Financial Management',
    description:
      'Manage double-entry accounting, automated IFRS 16 lease schedules, ROU asset depreciation, ledgers, and financial reporting in one platform.'
  },
  {
    title: 'Supply Chain & Inventory',
    description:
      'Manage purchasing, stock movements, warehouses, and inventory operations with connected business workflows.'
  },
  {
    title: 'Sales & CRM',
    description:
      'Manage quotations, sales invoices, flexible BNPL installment financing with automated IFRS 15 revenue recognition, and CRM workflows.'
  },
  {
    title: 'HR & Payroll',
    description:
      'Centralize employee records, HR workflows, leave management, and supported payroll operations.'
  }
];

const createCountryPage = ({
  country,
  slug,
  title,
  heroTitle,
  heroSubtitle,
  metaDescription,
  benefits,
  contentTitle,
  contentBody,
  contentPoints,
  faqs = [],
  features = baseFeatures
}) => ({
  slug,
  country,
  title,
  metaDescription,
  heroTitle,
  heroSubtitle,
  benefitsTitle: `Why Businesses in ${country} Choose FluxERP`,
  benefits,
  contentTitle,
  contentBody,
  contentPoints,
  featuresTitle: `Complete Business Management for ${country}`,
  featuresSubtitle:
    'Connect your business operations, financial workflows, and reporting through one integrated ERP platform.',
  features,
  faqs,
  breadcrumbs: [
    { name: 'Home', url: '/' },
    { name: title, url: `/${slug}` }
  ],
  ctaTitle: `Ready to Explore FluxERP in ${country}?`,
  ctaSubtitle:
    'Discover the capabilities and configuration options available for your business.',
  relatedPages: [
    { title: 'Accounting ERP', slug: 'accounting-erp' },
    { title: 'Manufacturing ERP', slug: 'manufacturing-erp' },
    { title: 'Retail ERP', slug: 'retail-erp' },
    { title: 'ERP Software', slug: 'erp-software' }
  ]
});

const countryBenefits = {
  UK: [
    {
      title: 'UK Business Accounting',
      description:
        'Manage business accounting and reporting workflows with configurable financial features.'
    },
    {
      title: 'Business Operations',
      description:
        'Connect purchasing, sales, inventory, and accounting to support daily business management.'
    },
    {
      title: 'Scalable Cloud Platform',
      description:
        'Support growing organizations through centralized business management and configurable access.'
    }
  ],

  Qatar: [
    {
      title: 'Business Management in Qatar',
      description:
        'Manage financial and operational workflows for businesses operating in Qatar.'
    },
    {
      title: 'Arabic & English Support',
      description:
        'Present supported user interfaces and business workflows according to the available language configuration.'
    },
    {
      title: 'Multi-Company Operations',
      description:
        'Manage relevant business entities and accounting workflows through configured company structures.'
    }
  ],

  Pakistan: [
    {
      title: 'Accounting for Pakistani Businesses',
      description:
        'Manage accounting, sales, purchasing, inventory, and business transactions through an integrated platform.'
    },
    {
      title: 'Retail, Trading & Manufacturing',
      description:
        'Support business operations across retail, wholesale, distribution, and manufacturing use cases.'
    },
    {
      title: 'Local Business Requirements',
      description:
        'Configure workflows and reporting according to business needs and applicable local requirements.'
    }
  ]
};

const countryFAQs = {
  UK: [
    {
      question: 'What is ERP software for UK businesses?',
      answer:
        'ERP software for UK businesses integrates accounting and business operations into a centralized platform. FluxERP provides relevant business management capabilities that organizations can evaluate against their accounting, operational, and compliance requirements.'
    },
    {
      question: 'Can FluxERP support UK accounting workflows?',
      answer:
        'FluxERP provides accounting and business management capabilities. UK businesses should verify the specific accounting treatments, tax integrations, and reporting functionality required for their operations.'
    }
  ],

  Qatar: [
    {
      question: 'What is ERP software for businesses in Qatar?',
      answer:
        'ERP software for Qatar businesses helps integrate accounting, sales, purchasing, inventory, and other business operations. FluxERP offers configurable business management capabilities for organizations evaluating an integrated ERP platform.'
    },
    {
      question: 'Does FluxERP support Arabic and English?',
      answer:
        'FluxERP is designed to support multilingual business interactions where the relevant language features are implemented. Confirm the current Arabic and English interface and workflow capabilities before deployment.'
    }
  ],

  Pakistan: [
    {
      question: 'What is ERP software for businesses in Pakistan?',
      answer:
        'ERP software for Pakistani businesses integrates accounting, inventory, sales, purchasing, and other business operations into a centralized system. FluxERP is designed to support SMEs and growing enterprises through configurable business management capabilities.'
    },
    {
      question: 'Can FluxERP support Pakistani trading and manufacturing businesses?',
      answer:
        'FluxERP provides relevant accounting, inventory, purchasing, sales, and manufacturing-related capabilities where implemented. Businesses should evaluate the available workflows against their specific operational and regulatory requirements.'
    },
    {
      question: 'Can FluxERP support Pakistani accounting and tax requirements?',
      answer:
        'FluxERP provides accounting functionality that businesses can evaluate against applicable Pakistani accounting and tax requirements. Specific FBR integrations, tax calculations, and compliance workflows must be confirmed against the current implementation.'
    }
  ]
};

// ==========================================
// INDUSTRY LANDING PAGES
// ==========================================

const manufacturingPage = {
  slug: 'manufacturing-erp',
  title: 'Manufacturing ERP Software',
  metaDescription:
    'Manufacturing ERP software for production planning, inventory, costing, and integrated financial management. Explore FluxERP for growing manufacturers.',
  heroTitle: 'Integrated ERP for Modern Manufacturing',
  heroSubtitle:
    'Connect production, inventory, purchasing, and financial workflows with FluxERP.',
  benefitsTitle: 'Manufacturing Business Management',
  benefits: [
    {
      title: 'Production Workflows',
      description:
        'Manage supported production processes and connect manufacturing activities with inventory and accounting.'
    },
    {
      title: 'Inventory & Materials',
      description:
        'Coordinate material requirements, inventory operations, and purchasing workflows.'
    },
    {
      title: 'Production Costing',
      description:
        'Support manufacturing cost analysis and financial visibility through configured accounting workflows.'
    }
  ],
  contentTitle: 'From Materials to Finished Goods',
  contentBody:
    'FluxERP helps manufacturers connect business operations across purchasing, inventory, production, and accounting. Configure the system according to your production processes and implemented capabilities.',
  contentPoints: [
    'Bill of Materials where implemented',
    'Work-in-Progress accounting',
    'Inventory and material management',
    'Production costing and reporting'
  ],
  featuresTitle: 'Manufacturing ERP Features',
  features: [
    ...baseFeatures,
    {
      title: 'Production Management',
      description:
        'Manage supported manufacturing workflows, production records, and related inventory operations.'
    }
  ],
  faqs: [
    {
      question: 'What is manufacturing ERP software?',
      answer:
        'Manufacturing ERP software connects production, inventory, purchasing, costing, and accounting processes in one system. FluxERP provides manufacturing-related capabilities based on the implemented modules and configuration.'
    },
    {
      question: 'Can FluxERP support manufacturing inventory and costing?',
      answer: 'FluxERP provides inventory and accounting capabilities, with manufacturing costing functionality depending on the implemented workflows and configuration.'
    }
  ]
};

const erpSoftwarePage = {
  slug: 'erp-software',
  title: 'Cloud ERP Software Solution',
  metaDescription:
    'Modern cloud ERP software solution. Unify accounting, inventory, supply chain, CRM, and HR on one intelligent business platform with FluxERP.',
  heroTitle: 'Complete Cloud ERP Software for Modern Businesses',
  heroSubtitle:
    'Connect every department, automate financial workflows, and scale operations with one unified ERP platform.',
  benefitsTitle: 'Why Choose FluxERP Cloud Solution',
  benefits: [
    {
      title: 'Unified Operations',
      description:
        'Eliminate disconnected systems by integrating accounting, purchasing, sales, inventory, and HR into a centralized source of truth.'
    },
    {
      title: 'Real-Time Visibility',
      description:
        'Make data-driven decisions with live financial reporting, inventory tracking, and executive dashboards.'
    },
    {
      title: 'Scalable Cloud Architecture',
      description:
        'Built for growth with multi-company, multi-currency, and secure role-based access control.'
    }
  ],
  contentTitle: 'End-to-End Enterprise Management',
  contentBody:
    'FluxERP replaces error-prone spreadsheets and fragmented software with a seamless, modular cloud ERP. From procurement and warehouse operations to double-entry general ledger, all business activities synchronize automatically in real time.',
  contentPoints: [
    'Multi-currency and multi-entity consolidation',
    'Automated purchase order to invoice workflows',
    'Real-time stock valuation and batch traceability',
    'Enterprise-grade security and full audit trails'
  ],
  featuresTitle: 'Core ERP Capabilities',
  features: baseFeatures,
  faqs: [
    {
      question: 'What is cloud ERP software?',
      answer:
        'Cloud ERP software is an integrated management suite accessible via web browser, enabling organizations to manage accounting, supply chain, inventory, and workforce without on-premise server maintenance.'
    },
    {
      question: 'How quickly can a business implement FluxERP?',
      answer:
        'FluxERP features modular onboarding with pre-built data import templates for charts of accounts, customers, vendors, and opening stock, enabling rapid phased deployment.'
    }
  ]
};

const accountingErpPage = {
  slug: 'accounting-erp',
  title: 'Accounting ERP Software',
  metaDescription:
    'Enterprise accounting ERP software for automated general ledger, accounts payable, accounts receivable, multi-currency transactions, and financial statements.',
  heroTitle: 'Intelligent Accounting & Financial ERP',
  heroSubtitle:
    'Streamline double-entry bookkeeping, automate multi-currency accounting, and generate audit-proof financial statements with FluxERP.',
  benefitsTitle: 'Accounting ERP Capabilities',
  benefits: [
    {
      title: 'Double-Entry General Ledger',
      description:
        'Maintain automated, real-time ledgers with configurable multi-level chart of accounts, journal templates, and full audit trails.'
    },
    {
      title: 'AP & AR Automation',
      description:
        'Accelerate 3-way invoice matching, manage aged debt, and automate vendor payments with cash flow visibility.'
    },
    {
      title: 'Multi-Currency Accounting',
      description:
        'Transact in any global currency with automated exchange rates, realized/unrealized FX gains & losses, and dual-currency reporting.'
    }
  ],
  contentTitle: 'Audit-Proof Financial Management',
  contentBody:
    'FluxERP Accounting delivers compliance-ready financial workflows conforming to IFRS and GAAP standards. Generate real-time balance sheets, income statements, trial balances, and cash flow forecasts with deep drill-down transaction analysis.',
  contentPoints: [
    'Automated bank reconciliation and feed matching',
    'Comprehensive Accounts Receivable and Payable aging',
    'IFRS 16 lease accounting and fixed asset depreciation',
    'Tax compliance with automated VAT and withholding tax tracking'
  ],
  featuresTitle: 'Financial Management Features',
  features: [
    {
      title: 'General Ledger',
      description: 'Hierarchical chart of accounts, journal vouchers, and automated double-entry postings.'
    },
    {
      title: 'Accounts Receivable & Payable',
      description: 'Customer credit limits, aged receivables, vendor invoices, and payment allocations.'
    },
    {
      title: 'Multi-Currency & FX',
      description: 'Real-time exchange rate updates, currency revaluation, and international trade accounting.'
    },
    {
      title: 'Financial Reporting',
      description: 'One-click Balance Sheet, Profit & Loss, Trial Balance, and Cash Flow Statements.'
    }
  ],
  faqs: [
    {
      question: 'Does FluxERP support standard accounting principles?',
      answer:
        'Yes, FluxERP is built on strict double-entry accounting principles compliant with IFRS and GAAP, ensuring complete auditability and transaction integrity.'
    },
    {
      question: 'Can FluxERP handle multi-currency bank accounts?',
      answer:
        'Yes, you can maintain bank and cash accounts in multiple currencies, reconcile statements, and automatically compute foreign exchange gain or loss upon settlement.'
    }
  ]
};

const aiPoweredErpPage = {
  slug: 'ai-powered-erp',
  title: 'AI-Powered ERP Software',
  metaDescription:
    'Next-generation AI-powered ERP platform. Automate invoice OCR, intelligent demand forecasting, ledger anomaly detection, and conversational analytics with FluxERP.',
  heroTitle: 'Intelligent AI-Powered ERP Platform',
  heroSubtitle:
    'Harness artificial intelligence and automation to eliminate manual data entry, forecast demand, and gain real-time operational insights.',
  benefitsTitle: 'AI-Driven Advantages',
  benefits: [
    {
      title: 'Intelligent Document OCR',
      description:
        'Automatically extract structured data from vendor bills, receipts, and invoices with high accuracy, eliminating manual typing.'
    },
    {
      title: 'Predictive Demand & Inventory',
      description:
        'Machine learning algorithms analyze historical sales cycles and seasonal patterns to suggest optimal stock reorder points.'
    },
    {
      title: 'Conversational Business Intelligence',
      description:
        'Ask questions in plain English and receive instant financial summaries, KPI visualizations, and operational recommendations.'
    }
  ],
  contentTitle: 'Transforming Enterprise Operations with AI',
  contentBody:
    'FluxERP brings pragmatic artificial intelligence directly into daily business operations. Instead of passive record-keeping, your ERP actively alerts managers to unusual ledger anomalies, impending stockouts, and revenue trends.',
  contentPoints: [
    'Automated document scanning, extraction, and ledger mapping',
    'Predictive cash flow and revenue trajectory modeling',
    'Real-time anomaly detection across general ledger transactions',
    'AI-assisted procurement recommendations based on supplier lead times'
  ],
  featuresTitle: 'AI-Enhanced Capabilities',
  features: [
    {
      title: 'Smart Invoice Capture',
      description: 'OCR scanning transforms supplier PDFs into verified purchase orders and bills.'
    },
    {
      title: 'Predictive Inventory',
      description: 'Automated reordering suggestions based on lead times and historical burn rates.'
    },
    {
      title: 'Automated Reconciliations',
      description: 'Intelligent matching rules accelerate bank and ledger reconciliations.'
    },
    {
      title: 'Natural Language Analytics',
      description: 'Instant querying of financial data, sales performance, and operational health.'
    }
  ],
  faqs: [
    {
      question: 'How does AI improve ERP productivity?',
      answer:
        'AI eliminates tedious data entry, reduces human error in expense categorization, and proactively alerts decision makers to operational bottlenecks before they impact profits.'
    },
    {
      question: 'Is proprietary business data secure when using AI?',
      answer:
        'Yes. All AI models operate in dedicated secure environments with strict data isolation. Your enterprise financial records are never shared or used to train public models.'
    }
  ]
};

export const landingPagesConfig = [
  erpSoftwarePage,
  accountingErpPage,
  aiPoweredErpPage,
  manufacturingPage,

  // COUNTRY-SPECIFIC ERP PAGES
  createCountryPage({
    country: 'UK',
    slug: 'erp-software-uk',
    title: 'Cloud ERP Software in the UK',
    metaDescription:
      'Explore cloud ERP software for UK businesses. Manage accounting, inventory, purchasing, sales, and business operations with FluxERP.',
    heroTitle: 'Cloud ERP Software for UK Businesses',
    heroSubtitle:
      'Connect your accounting and business operations through an integrated ERP platform designed for growing organizations.',
    benefits: countryBenefits.UK,
    contentTitle: 'Manage Your UK Business in One Platform',
    contentBody:
      'FluxERP helps organizations centralize financial and operational workflows. Review the available modules and configure the platform around your business requirements.',
    contentPoints: [
      'Accounting and financial workflows',
      'Purchasing and supplier management',
      'Inventory and sales operations',
      'Configurable business reporting'
    ],
    faqs: countryFAQs.UK
  }),

  createCountryPage({
    country: 'Qatar',
    slug: 'erp-software-qatar',
    title: 'ERP Software in Qatar',
    metaDescription:
      'Explore ERP software for businesses in Qatar. Manage accounting, trading, inventory, sales, and business operations with FluxERP.',
    heroTitle: 'ERP Software for Businesses in Qatar',
    heroSubtitle:
      'Manage your financial and operational workflows through an integrated ERP platform for growing businesses in Qatar.',
    benefits: countryBenefits.Qatar,
    contentTitle: 'Business Management for Qatar',
    contentBody:
      'FluxERP provides integrated business management capabilities that organizations in Qatar can evaluate against their accounting, operational, and regional requirements.',
    contentPoints: [
      'Financial and accounting management',
      'Trading and distribution workflows',
      'Multi-company capabilities where enabled',
      'Supported language and regional configurations'
    ],
    faqs: countryFAQs.Qatar
  }),

  createCountryPage({
    country: 'Pakistan',
    slug: 'erp-software-pakistan',
    title: 'ERP Software in Pakistan',
    metaDescription:
      'Explore cloud ERP software in Pakistan for accounting, manufacturing, retail, trading, distribution, and growing businesses with FluxERP.',
    heroTitle: 'Cloud ERP Software for Pakistani Businesses',
    heroSubtitle:
      'Bring accounting, inventory, sales, purchasing, and business operations together with FluxERP.',
    benefits: countryBenefits.Pakistan,
    contentTitle: 'Built for Growing Businesses in Pakistan',
    contentBody:
      'FluxERP provides integrated business management capabilities for organizations in Pakistan. Explore relevant modules and configure the platform around your industry and operational requirements.',
    contentPoints: [
      'Accounting and financial management',
      'Retail, wholesale, and distribution workflows',
      'Manufacturing and inventory management',
      'Configurable business reporting'
    ],
    faqs: countryFAQs.Pakistan
  }),

  // COUNTRY-SPECIFIC ACCOUNTING ERP PAGES
  createCountryPage({
    country: 'UK',
    slug: 'accounting-erp-uk',
    title: 'UK Accounting ERP Software',
    metaDescription:
      'Cloud accounting ERP software for UK businesses. Manage Making Tax Digital (MTD), VAT returns, multi-currency ledgers, and UK GAAP financial reporting with FluxERP.',
    heroTitle: 'Cloud Accounting ERP for UK Businesses',
    heroSubtitle:
      'Built for UK financial compliance, VAT reporting, Making Tax Digital workflows, and multi-currency business operations.',
    benefits: countryBenefits.UK,
    contentTitle: 'Tailored for UK Financial Workflows',
    contentBody:
      'FluxERP provides UK businesses with an integrated financial suite designed to handle UK VAT rules, Making Tax Digital (MTD) preparation, automated GBP accounting, and bank feed reconciliations.',
    contentPoints: [
      'UK VAT return preparation & rate management (Standard, Reduced, Zero-rated)',
      'Making Tax Digital (MTD) aligned digital record keeping',
      'HMRC-compliant audit logs and double-entry transaction histories',
      'Multi-currency ledgers with automated daily GBP conversions'
    ],
    faqs: countryFAQs.UK
  }),

  createCountryPage({
    country: 'Qatar',
    slug: 'accounting-erp-qatar',
    title: 'Qatar Accounting ERP Software',
    metaDescription:
      'Accounting ERP software for businesses in Qatar. Dual-currency QAR reporting, Arabic & English interfaces, WHT compliance, and integrated financial management with FluxERP.',
    heroTitle: 'Accounting & Financial ERP for Qatar',
    heroSubtitle:
      'Manage QAR ledgers, bilingual invoicing, and financial compliance for companies operating in the State of Qatar.',
    benefits: countryBenefits.Qatar,
    contentTitle: 'Designed for Qatar Financial Requirements',
    contentBody:
      'FluxERP empowers Qatar enterprises with robust QAR accounting, compliant commercial invoicing, bilingual Arabic/English documentation, and integrated audit controls.',
    contentPoints: [
      'Dual-currency QAR and foreign currency ledgers',
      'Bilingual Arabic & English commercial invoices and receipts',
      'Tax and withholding compliance workflows',
      'Multi-company and multi-branch consolidation across Qatar'
    ],
    faqs: countryFAQs.Qatar
  }),

  createCountryPage({
    country: 'Pakistan',
    slug: 'accounting-erp-pakistan',
    title: 'Pakistan Accounting ERP Software',
    metaDescription:
      'Accounting ERP software in Pakistan. FBR sales tax reporting, withholding tax (WHT) calculations, multi-currency ledgers, and automated financial management with FluxERP.',
    heroTitle: 'Accounting & Financial ERP for Pakistani Businesses',
    heroSubtitle:
      'Automate PKR accounting, FBR sales tax compliance, withholding tax deductions, and financial statements with FluxERP.',
    benefits: countryBenefits.Pakistan,
    contentTitle: 'Complete Financial Management for Pakistan',
    contentBody:
      'FluxERP provides Pakistani enterprises and SMEs with specialized tools for FBR tax calculation, section-wise withholding tax tracking, automated vendor payments, and bank reconciliations.',
    contentPoints: [
      'FBR Sales Tax & Provincial Sales Tax (PRA, SRB, KPRA, BRA) workflows',
      'Withholding tax (WHT) calculation and certificate generation',
      'Automated PKR double-entry ledger & chart of accounts',
      'Multi-branch cash and bank management'
    ],
    faqs: countryFAQs.Pakistan
  }),

  // COUNTRY-SPECIFIC MANUFACTURING ERP PAGES
  createCountryPage({
    country: 'UK',
    slug: 'manufacturing-erp-uk',
    title: 'Manufacturing ERP Software UK',
    metaDescription:
      'UK manufacturing ERP software for production planning, Bill of Materials, shop floor control, and inventory costing. Discover FluxERP for UK manufacturers.',
    heroTitle: 'Manufacturing ERP for UK Industry',
    heroSubtitle:
      'Connect UK manufacturing operations from Bill of Materials and Work in Progress to finished goods costing and accounting.',
    benefits: countryBenefits.UK,
    contentTitle: 'Precision Manufacturing for UK Businesses',
    contentBody:
      'FluxERP helps UK manufacturers optimize shop floor efficiency, track material consumption, control production lead times, and calculate accurate landed costs.',
    contentPoints: [
      'Multi-level Bill of Materials (BOM)',
      'Work order routing and machine scheduling',
      'Live scrap and yield tracking',
      'Integrated manufacturing financial costing'
    ],
    faqs: countryFAQs.UK
  }),

  createCountryPage({
    country: 'Qatar',
    slug: 'manufacturing-erp-qatar',
    title: 'Manufacturing ERP Software Qatar',
    metaDescription:
      'Manufacturing ERP software in Qatar for industrial plants, fabrication, production scheduling, and inventory costing with FluxERP.',
    heroTitle: 'Industrial & Manufacturing ERP for Qatar',
    heroSubtitle:
      'Streamline industrial manufacturing, raw material procurement, and production planning for factories in Qatar.',
    benefits: countryBenefits.Qatar,
    contentTitle: 'Empowering Qatar Industrial Operations',
    contentBody:
      'Designed to support industrial diversification and manufacturing in Qatar, FluxERP unifies raw materials inventory, work orders, machinery maintenance, and cost tracking.',
    contentPoints: [
      'Raw material inventory and import logistics',
      'Production order tracking and labor allocation',
      'Industrial batch and lot traceability',
      'Bilingual Arabic/English production documentation'
    ],
    faqs: countryFAQs.Qatar
  }),

  createCountryPage({
    country: 'Pakistan',
    slug: 'manufacturing-erp-pakistan',
    title: 'Manufacturing ERP Software Pakistan',
    metaDescription:
      'Manufacturing ERP software in Pakistan for textile, chemical, engineering, and FMCG factories. Manage BOM, costing, inventory, and accounting with FluxERP.',
    heroTitle: 'Manufacturing ERP for Pakistani Industry',
    heroSubtitle:
      'Optimize factory production, raw material consumption, batch costing, and supply chains for manufacturers across Pakistan.',
    benefits: countryBenefits.Pakistan,
    contentTitle: 'Built for Manufacturing in Pakistan',
    contentBody:
      'From Karachi to Punjab industrial hubs, FluxERP helps Pakistani manufacturers reduce material wastage, automate piece-rate and production payroll, and calculate real-time batch costs.',
    contentPoints: [
      'Bill of Materials and process manufacturing',
      'Work-in-progress (WIP) and scrap tracking',
      'Import raw material landed costing',
      'Integrated factory financial ledgers'
    ],
    faqs: countryFAQs.Pakistan
  }),

  // COUNTRY-SPECIFIC TRADING ERP PAGES
  createCountryPage({
    country: 'UK',
    slug: 'trading-erp-uk',
    title: 'Trading & Wholesale ERP Software UK',
    metaDescription:
      'Trading ERP software for UK importers, exporters, and wholesale distributors. Manage landed costs, multi-currency trade, inventory, and VAT with FluxERP.',
    heroTitle: 'Cloud ERP for UK Trading & Distribution',
    heroSubtitle:
      'Streamline import/export trade, container shipment tracking, multi-currency purchases, and UK wholesale distribution.',
    benefits: countryBenefits.UK,
    contentTitle: 'Optimize UK Trading Operations',
    contentBody:
      'FluxERP simplifies complex international and domestic trading workflows for UK merchants, with automated customs landed cost calculations, purchase orders, and sales fulfillment.',
    contentPoints: [
      'Import landed cost allocation (freight, duties, insurance)',
      'Multi-currency purchase and sales orders',
      'Multi-warehouse stock allocation and dispatch',
      'Automated trade invoicing and UK VAT tracking'
    ],
    faqs: countryFAQs.UK
  }),

  createCountryPage({
    country: 'Qatar',
    slug: 'trading-erp-qatar',
    title: 'Trading & Distribution ERP Software Qatar',
    metaDescription:
      'Trading ERP software in Qatar for import/export companies, general trading, building materials, and distribution businesses with FluxERP.',
    heroTitle: 'Trading ERP for Businesses in Qatar',
    heroSubtitle:
      'Manage commercial trading, port shipments, wholesale distribution, and multi-currency transactions in Qatar.',
    benefits: countryBenefits.Qatar,
    contentTitle: 'Drive Trading Excellence in Qatar',
    contentBody:
      'FluxERP provides trading enterprises in Qatar with complete visibility over supplier shipments, warehouse stocks, wholesale pricing tiers, and credit management.',
    contentPoints: [
      'Shipment and container tracking',
      'Bilingual delivery notes and sales invoices',
      'Customer credit limits and aging balances',
      'Multi-location warehouse inventory control'
    ],
    faqs: countryFAQs.Qatar
  }),

  createCountryPage({
    country: 'Pakistan',
    slug: 'trading-erp-pakistan',
    title: 'Trading ERP Software Pakistan',
    metaDescription:
      'Trading and distribution ERP software in Pakistan. Manage wholesale supply chains, inventory, sales orders, FBR sales tax, and recovery with FluxERP.',
    heroTitle: 'Trading & Distribution ERP for Pakistan',
    heroSubtitle:
      'Manage wholesale trading, distribution routes, customer credit recovery, and inventory across Pakistan.',
    benefits: countryBenefits.Pakistan,
    contentTitle: 'Built for Pakistan Wholesale & Trading',
    contentBody:
      'FluxERP helps Pakistani distributors and general trading companies automate order booking, track delivery van inventory, enforce customer credit terms, and reconcile supplier accounts.',
    contentPoints: [
      'Wholesale and distributor pricing schemes',
      'Customer credit limit checks and recovery tracking',
      'Multi-godown stock transfers and tracking',
      'Integrated FBR sales tax invoices and reports'
    ],
    faqs: countryFAQs.Pakistan
  }),

  // INDUSTRY-SPECIFIC PAGES
  {
    slug: 'retail-erp',
    title: 'Retail ERP & POS Software',
    metaDescription:
      'Retail ERP software with supported POS, inventory, sales, and accounting workflows for retail stores and growing businesses.',
    heroTitle: 'Unified Retail Management',
    heroSubtitle:
      'Connect retail sales, inventory, customer management, and financial operations through FluxERP.',
    benefitsTitle: 'Retail ERP Capabilities',
    benefits: [
      {
        title: 'Point of Sale',
        description:
          'Manage supported retail sales and payment workflows through the integrated POS system.'
      },
      {
        title: 'Inventory Visibility',
        description:
          'Connect retail inventory processes with purchasing and business operations.'
      },
      {
        title: 'Integrated Accounting',
        description:
          'Connect supported sales and payment transactions with financial records.'
      }
    ],
    contentTitle: 'Manage Retail Operations',
    contentBody:
      'FluxERP supports retail-related business processes through configured POS, inventory, sales, and accounting functionality.',
    contentPoints: [
      'POS sales workflows',
      'Inventory and stock management',
      'Customer and sales records',
      'Integrated financial operations'
    ],
    featuresTitle: 'Retail ERP Features',
    features: baseFeatures,
    faqs: [
      {
        question: 'What is retail ERP software?',
        answer:
          'Retail ERP software integrates retail sales, inventory, purchasing, accounting, and other business operations. FluxERP provides relevant retail and POS capabilities where implemented.'
      }
    ]
  },

  {
    slug: 'trading-erp',
    title: 'Trading ERP Software',
    metaDescription:
      'Trading ERP software for purchasing, sales, inventory, financial management, and business operations with FluxERP.',
    heroTitle: 'ERP for Trading Businesses',
    heroSubtitle:
      'Connect purchasing, inventory, sales, and accounting workflows for trading operations.',
    benefitsTitle: 'Trading ERP Capabilities',
    benefits: [
      {
        title: 'Purchase & Sales Management',
        description:
          'Manage supported purchasing, supplier, customer, and sales workflows.'
      },
      {
        title: 'Inventory Control',
        description:
          'Connect stock management with purchasing and sales operations.'
      },
      {
        title: 'Financial Visibility',
        description:
          'Integrate supported trading transactions with accounting and reporting.'
      }
    ],
    contentTitle: 'Manage Your Trading Operations',
    contentBody:
      'FluxERP helps trading businesses centralize operational and financial processes through an integrated ERP platform.',
    contentPoints: [
      'Purchase and sales workflows',
      'Inventory management',
      'Customer and supplier management',
      'Accounting integration'
    ],
    featuresTitle: 'Trading ERP Features',
    features: baseFeatures
  },

  {
    slug: 'wholesale-erp',
    title: 'Wholesale ERP Software',
    metaDescription:
      'Wholesale ERP software for inventory, purchasing, sales, customer management, and accounting operations with FluxERP.',
    heroTitle: 'Wholesale Business Management',
    heroSubtitle:
      'Manage wholesale purchasing, inventory, sales, and accounting through one integrated ERP platform.',
    benefitsTitle: 'Wholesale ERP Benefits',
    benefits: [
      {
        title: 'Wholesale Inventory',
        description:
          'Manage supported inventory and warehouse workflows for wholesale operations.'
      },
      {
        title: 'Customer & Supplier Management',
        description:
          'Connect business relationships with purchasing and sales processes.'
      },
      {
        title: 'Financial Management',
        description:
          'Integrate supported wholesale transactions with accounting workflows.'
      }
    ],
    contentTitle: 'Connect Wholesale Operations',
    contentBody:
      'FluxERP provides integrated business management capabilities for wholesale organizations, subject to the available configuration.',
    contentPoints: [
      'Wholesale sales workflows',
      'Purchase management',
      'Inventory operations',
      'Financial reporting'
    ],
    featuresTitle: 'Wholesale ERP Features',
    features: baseFeatures
  },

  {
    slug: 'distribution-erp',
    title: 'Distribution ERP Software',
    metaDescription:
      'Distribution ERP software for inventory, purchasing, sales, warehouse operations, and integrated accounting with FluxERP.',
    heroTitle: 'ERP for Distribution Businesses',
    heroSubtitle:
      'Connect inventory, purchasing, sales, and financial workflows for distribution operations.',
    benefitsTitle: 'Distribution Management',
    benefits: [
      {
        title: 'Inventory & Warehouses',
        description:
          'Manage supported warehouse and inventory workflows.'
      },
      {
        title: 'Purchasing & Sales',
        description:
          'Connect procurement and sales operations through integrated business processes.'
      },
      {
        title: 'Accounting Integration',
        description:
          'Support financial recording for configured distribution transactions.'
      }
    ],
    contentTitle: 'Manage Distribution Operations',
    contentBody:
      'FluxERP supports distribution-related business processes through its available inventory, purchasing, sales, and accounting capabilities.',
    contentPoints: [
      'Warehouse management',
      'Purchase and sales operations',
      'Inventory visibility',
      'Business reporting'
    ],
    featuresTitle: 'Distribution ERP Features',
    features: baseFeatures
  },

  {
    slug: 'textile-erp',
    title: 'Textile ERP Software',
    metaDescription:
      'Textile ERP software for manufacturing, inventory, costing, and accounting workflows. Explore FluxERP for textile businesses.',
    heroTitle: 'ERP for Textile Businesses',
    heroSubtitle:
      'Connect textile manufacturing, inventory, purchasing, and financial workflows through FluxERP.',
    benefitsTitle: 'Textile Business Management',
    benefits: [
      {
        title: 'Manufacturing Workflows',
        description:
          'Manage supported textile manufacturing processes through configured production capabilities.'
      },
      {
        title: 'Materials & Inventory',
        description:
          'Connect materials management with inventory and purchasing operations.'
      },
      {
        title: 'Costing & Accounting',
        description:
          'Support financial visibility through available costing and accounting functionality.'
      }
    ],
    contentTitle: 'Connect Textile Operations',
    contentBody:
      'FluxERP can support relevant textile business processes where the required manufacturing and inventory features are implemented.',
    contentPoints: [
      'Production and inventory workflows',
      'Material management',
      'Costing and financial operations',
      'Business reporting'
    ],
    featuresTitle: 'Textile ERP Features',
    features: baseFeatures
  },

  {
    slug: 'pharmaceutical-erp',
    title: 'Pharmaceutical ERP Software',
    metaDescription:
      'Pharmaceutical ERP software for supported inventory, purchasing, sales, manufacturing, and accounting workflows with FluxERP.',
    heroTitle: 'ERP for Pharmaceutical Businesses',
    heroSubtitle:
      'Connect supported pharmaceutical trading and manufacturing operations through an integrated ERP platform.',
    benefitsTitle: 'Pharmaceutical Business Management',
    benefits: [
      {
        title: 'Inventory Operations',
        description:
          'Manage supported stock and inventory workflows for pharmaceutical businesses.'
      },
      {
        title: 'Purchasing & Sales',
        description:
          'Connect supplier, purchasing, customer, and sales processes.'
      },
      {
        title: 'Financial Integration',
        description:
          'Link supported business transactions with accounting workflows.'
      }
    ],
    contentTitle: 'Manage Pharmaceutical Operations',
    contentBody:
      'FluxERP can support relevant pharmaceutical business workflows where the required features are implemented. Verify batch, expiry, traceability, and regulatory requirements before deployment.',
    contentPoints: [
      'Inventory and purchasing workflows',
      'Sales and customer management',
      'Supported manufacturing processes',
      'Accounting and reporting'
    ],
    featuresTitle: 'Pharmaceutical ERP Features',
    features: baseFeatures
  },

  {
    slug: 'fmcg-erp',
    title: 'FMCG ERP Software',
    metaDescription:
      'FMCG ERP software for inventory, sales, purchasing, distribution, and accounting workflows with FluxERP.',
    heroTitle: 'ERP for FMCG Businesses',
    heroSubtitle:
      'Manage FMCG trading, distribution, inventory, and financial operations through FluxERP.',
    benefitsTitle: 'FMCG ERP Capabilities',
    benefits: [
      {
        title: 'Inventory Management',
        description:
          'Manage supported inventory and stock workflows for FMCG operations.'
      },
      {
        title: 'Sales & Distribution',
        description:
          'Connect sales and distribution-related business processes.'
      },
      {
        title: 'Financial Management',
        description:
          'Integrate supported transactions with accounting and reporting.'
      }
    ],
    contentTitle: 'Connect FMCG Operations',
    contentBody:
      'FluxERP provides relevant business management capabilities for FMCG organizations based on available modules and configuration.',
    contentPoints: [
      'Inventory and stock management',
      'Purchasing and sales workflows',
      'Distribution operations',
      'Accounting integration'
    ],
    featuresTitle: 'FMCG ERP Features',
    features: baseFeatures
  },

  {
    slug: 'hr-payroll-erp',
    title: 'HR & Payroll ERP Software',
    metaDescription:
      'HR and payroll ERP software for employee management, leave workflows, payroll operations, and integrated business management with FluxERP.',
    heroTitle: 'Integrated HR & Payroll Management',
    heroSubtitle:
      'Centralize employee management and supported payroll workflows through FluxERP.',
    benefitsTitle: 'HR & Payroll Capabilities',
    benefits: [
      {
        title: 'Employee Management',
        description:
          'Manage supported employee records and HR workflows in one platform.'
      },
      {
        title: 'Leave & Attendance',
        description:
          'Support relevant employee leave and attendance processes where implemented.'
      },
      {
        title: 'Payroll Operations',
        description:
          'Manage supported payroll workflows according to configuration and applicable requirements.'
      }
    ],
    contentTitle: 'Connect HR and Business Operations',
    contentBody:
      'FluxERP provides HR and payroll capabilities where implemented. Payroll calculations and statutory requirements must be verified for the applicable jurisdiction.',
    contentPoints: [
      'Employee records',
      'Leave management',
      'Supported payroll workflows',
      'HR reporting'
    ],
    featuresTitle: 'HR & Payroll ERP Features',
    features: baseFeatures
  },

  {
    slug: 'construction-erp',
    title: 'Construction ERP Software',
    metaDescription:
      'Construction ERP software for accounting, procurement, inventory, and supported project business workflows with FluxERP.',
    heroTitle: 'ERP for Construction Businesses',
    heroSubtitle:
      'Connect purchasing, accounting, inventory, and relevant operational workflows for construction organizations.',
    benefitsTitle: 'Construction ERP Capabilities',
    benefits: [
      {
        title: 'Procurement Management',
        description:
          'Manage supported purchasing and supplier-related workflows.'
      },
      {
        title: 'Financial Management',
        description:
          'Connect supported business transactions with accounting and reporting.'
      },
      {
        title: 'Inventory Operations',
        description:
          'Manage relevant materials and inventory processes where implemented.'
      }
    ],
    contentTitle: 'Manage Construction Business Operations',
    contentBody:
      'FluxERP provides business management capabilities that construction companies can evaluate against their operational and project requirements.',
    contentPoints: [
      'Procurement workflows',
      'Inventory management',
      'Accounting and reporting',
      'Relevant project functionality where implemented'
    ],
    featuresTitle: 'Construction ERP Features',
    features: baseFeatures
  }
];

export default landingPagesConfig;
